// src/pages/IntelligencePage.js
//
// Route: /intelligence
// The hub — Spain overview, IRPF calculator, and US×Spain assessment cards.
// Assessment generation and viewing lives at /assessment.

import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useSubject } from '../context/SubjectContext'
import { supabase } from '../lib/supabase'
import { resumeCalculatorDraft } from '../lib/resumeCalculatorDraft'

// ─── Helpers (mirrored from deriveFacts.ts for frontend use) ──────────────────

function checkIsUsPerson(citizenshipStatus) {
  return ['us_citizen', 'green_card', 'citizen', 'both'].includes(citizenshipStatus || '')
}

function deriveSpainSnapshot(taxProfile, yearProfile) {
  const residency = yearProfile?.spain_residency_status
  const residencyLabels = {
    resident_full_year:    'Spanish tax resident',
    resident_partial_year: 'Spanish tax resident (partial year)',
    non_resident:          'Non-resident',
    uncertain:             'Residency unclear',
  }

  const primaryReturn = residency === 'non_resident'
    ? 'Modelo 210 (IRNR)'
    : ['resident_full_year','resident_partial_year'].includes(residency)
      ? (taxProfile?.beckham_status === 'approved' ? 'Modelo 151 (Beckham)' : 'IRPF (Modelo 100)')
      : null

  const keyDeadline = residency === 'non_resident'
    ? 'Quarterly (Modelo 210)'
    : ['resident_full_year','resident_partial_year'].includes(residency)
      ? '30 June'
      : null

  return {
    residencyLabel: residencyLabels[residency] || null,
    primaryReturn,
    keyDeadline,
    complexity: yearProfile?.complexity_tier || null,
  }
}

function buildCrossBorderRationale(taxProfile, yearProfile) {
  const citizenship = taxProfile?.citizenship_status
  const residency   = yearProfile?.spain_residency_status
  const incomeTypes = yearProfile?.income_types || []
  const foreignBand = yearProfile?.foreign_account_band
  const sentences   = []

  if (['us_citizen','citizen','green_card','both'].includes(citizenship || '')) {
    sentences.push(
      citizenship === 'green_card'
        ? 'As a green card holder, US worldwide tax reporting generally continues even while living in Spain.'
        : 'As a US citizen, US worldwide tax reporting generally continues even while living in Spain.'
    )
  }

  if (['resident_full_year','resident_partial_year'].includes(residency || '')) {
    sentences.push(
      'Because your profile also indicates Spanish tax residency, the same income and assets may need to be understood through both systems.'
    )
  }

  const highlights = []
  if (foreignBand && !['none','under_10k'].includes(foreignBand)) highlights.push('foreign accounts')
  if (incomeTypes.includes('self_employment')) highlights.push('self-employment')
  if (incomeTypes.includes('employment'))      highlights.push('employment income')
  if (incomeTypes.includes('rental'))          highlights.push('rental property')

  sentences.push(
    highlights.length > 0
      ? `The full US × Spain assessment focuses on that overlap — especially ${highlights.join(', ')}, tax credits, and filing timing.`
      : 'The full US × Spain assessment covers that overlap — including foreign account reporting, tax credits, and filing coordination.'
  )

  return sentences
}

// ─── IntelligencePage ─────────────────────────────────────────────────────────

export default function IntelligencePage() {
  const { session, user }                = useAuth()
  const { activeSubject, activeTaxYear } = useSubject()
  const navigate                         = useNavigate()

  const [loading,  setLoading]  = useState(true)
  const [hubData,  setHubData]  = useState(null)
  const [hasAssessment, setHasAssessment] = useState(false)
  const [assessmentDate, setAssessmentDate] = useState(null)
  // Full list of the user's own published, non-deleted calculator sessions —
  // separate from hubData.calculatorSession (which is just the most recent
  // one, used for the main card). Only fetched/shown to let the user manage
  // (delete) past estimates; the main card's "current" figure is unaffected
  // by anything here except which rows still exist.
  const [pastEstimates, setPastEstimates] = useState([])
  const [deletingId, setDeletingId] = useState(null)

  const activeSubjectId = activeSubject?.id || null

  // Guards a one-time silent retry when calculator_sessions has no row yet.
  // Most common cause: the user lands here immediately after an OTP + claim-session
  // sequence on a different page mount — claim-session is awaited there, but
  // Supabase write propagation can still race this page's first query. Reset on
  // each effect re-run (new user/subject) so the retry is available again.
  const calcRetriedRef = useRef(false)

  useEffect(() => {
    calcRetriedRef.current = false
    console.log('IntelligencePage effect:', { userId: user?.id, activeSubjectId, activeTaxYear })
    if (!user || !activeSubjectId) {
      // Previously this returned with no further action, which left
      // `loading: true` permanently whenever this effect's FIRST run
      // landed before `user`/`activeSubjectId` resolved (e.g. right after
      // OTP verification, before AuthContext's async onAuthStateChange
      // listener updates `user`). `user` also wasn't in the dependency
      // array below, so even once it resolved a moment later there was no
      // guarantee this effect would re-run. Net effect: an infinite
      // spinner that only cleared on a full page refresh.
      //
      // Once `user` exists but there's genuinely no active subject yet
      // (new account, nothing claimed), that's a real, resolved state —
      // not a still-loading one — so we stop spinning and let the page
      // render its normal "nothing yet" defaults instead of hanging.
      if (user) setLoading(false)
      return
    }

    // Build assessment query before calling .maybeSingle() so filters chain correctly
    const asmtQuery = supabase.from('assessments')
      .select('id, generated_at')
      .order('generated_at', { ascending: false })
      .limit(1)
    if (activeTaxYear) asmtQuery.eq('tax_year', activeTaxYear)
    if (activeSubject?.subject_type === 'self') {
      asmtQuery.or(`subject_id.eq.${activeSubjectId},subject_id.is.null`).eq('user_id', user.id)
    } else {
      asmtQuery.eq('subject_id', activeSubjectId)
    }

    Promise.all([
      supabase.from('tax_profiles')
        .select('citizenship_status, beckham_status')
        .eq('subject_id', activeSubjectId)
        .maybeSingle(),
      supabase.from('tax_year_profiles')
        .select('spain_residency_status, complexity_tier, income_types, foreign_account_band')
        .eq('subject_id', activeSubjectId)
        .eq('tax_year', activeTaxYear)
        .maybeSingle(),
      supabase.from('triage_sessions')
        .select('public_token, triage_result')
        .eq('tax_subject_id', activeSubjectId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase.from('calculator_sessions')
        .select('public_token, irpf_gross, estimate_low, estimate_high, confidence, tax_year, estimate_result')
        .eq('owner_user_id', user.id)
        // BUG FIX: an unpublished draft has no public_token yet, but was
        // still matching this query (it only filtered on owner_user_id).
        // calc's truthiness alone drives the "✓ Complete / View your
        // estimate" branch below, so a draft-only user got a real,
        // clickable link to /calculator/result/null. Filtering to
        // published rows here means an unpublished draft simply doesn't
        // count as "done" yet — consistent with drafts not being
        // first-class, shareable artifacts anywhere else in the app.
        .not('published_at', 'is', null)
        // Soft-deleted sessions (calculator_sessions.deleted_at) must not
        // count as "done" either — same reasoning as the published_at
        // filter above, just for the newer deletion feature.
        .is('deleted_at', null)
        // FIX: was ordering by created_at, inconsistent with every other
        // "most recent published session" lookup built during the
        // is_representative retirement (generate-case-brief,
        // get-advisor-pipeline, and the past-estimates list query below all
        // order by published_at). created_at and published_at can diverge
        // — a session created earlier but published later should be "the"
        // current one — and this mismatch is exactly what made the main
        // card show a different, older session than the top of the
        // "Your saved estimates" list.
        .order('published_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
      asmtQuery.maybeSingle(),
      // Full list, not just the most recent — powers the "your other saved
      // estimates" delete-management list. Separate from the single-row
      // query above (which drives the main card) so a delete here never
      // has to reason about which one is "the" card figure. Deliberately
      // includes DRAFTS (no published_at filter) — per John's own
      // draft_publish_framework.md, a claimed draft the user never
      // publishes is currently an orphaned row with no UI path back to it
      // at all; this list is the first place that becomes visible, even
      // without a full resume-draft feature.
      supabase.from('calculator_sessions')
        .select('id, public_token, tax_year, irpf_gross, is_autonomo, published_at, created_at')
        .eq('owner_user_id', user.id)
        .is('deleted_at', null)
        .order('created_at', { ascending: false }),
    ]).then(([profileRes, yearRes, triageRes, calcRes, asmtRes, pastRes]) => {
      const taxProfile        = profileRes.data || null
      const yearProfile       = yearRes.data    || null
      const triageToken       = triageRes.data?.public_token || null
      const calculatorSession = calcRes.data    || null

      const isUsPerson = triageRes.data?.triage_result?.derived_facts?.isUsPerson
        ?? checkIsUsPerson(taxProfile?.citizenship_status)
      const snapshot   = deriveSpainSnapshot(taxProfile, yearProfile)
      const rationale  = isUsPerson ? buildCrossBorderRationale(taxProfile, yearProfile) : []

      setHubData({ taxProfile, yearProfile, triageToken, isUsPerson, snapshot, rationale, calculatorSession })
      setPastEstimates(pastRes.data || [])
      if (asmtRes.data) {
        setHasAssessment(true)
        setAssessmentDate(asmtRes.data.generated_at)
      }
      setLoading(false)

      // Silent retry: if no calc session found on first load and user is authenticated,
      // try once more after 2s. Handles the race between claim-session writing
      // owner_user_id and this page's initial query (most likely after OTP auth on
      // a different page, followed by navigating here via the app nav).
      if (!calculatorSession && !calcRetriedRef.current) {
        calcRetriedRef.current = true
        setTimeout(async () => {
          const { data } = await supabase
            .from('calculator_sessions')
            .select('public_token, irpf_gross, estimate_low, estimate_high, confidence, tax_year, estimate_result')
            .eq('owner_user_id', user.id)
            // Same published_at and deleted_at filters as the main query
            // above — see those comments for why an unpublished draft or a
            // deleted session must not satisfy this.
            .not('published_at', 'is', null)
            .is('deleted_at', null)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle()
          if (data) {
            setHubData(prev => prev ? { ...prev, calculatorSession: data } : prev)
          }
        }, 2000)
      }
    }).catch(err => {
      console.error('IntelligencePage load error:', err)
      setLoading(false)
    })
  }, [user, activeSubjectId, activeSubject?.subject_type, activeTaxYear])

  // Soft-deletes via the new delete-calculator-session function, then drops
  // the row from local state — no full refetch needed, since the function
  // is the only source of truth for whether the delete actually landed.
  async function handleDeleteEstimate(sessionId) {
    if (deletingId) return
    setDeletingId(sessionId)
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession()
      if (!authSession) throw new Error('Not authenticated')

      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-calculator-session`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authSession.access_token}` },
          body: JSON.stringify({ session_id: sessionId }),
        }
      )
      const json = await res.json()
      if (!res.ok || !json.deleted) throw new Error(json.error || 'Failed to delete estimate')

      setPastEstimates(prev => prev.filter(s => s.id !== sessionId))
      // If the deleted session was also the main card's current one, drop
      // it from hubData too — the card falls back to its "not done yet"
      // state until the user picks another from the list or runs a new one.
      setHubData(prev => (prev?.calculatorSession?.public_token &&
        pastEstimates.find(s => s.id === sessionId)?.public_token === prev.calculatorSession.public_token)
        ? { ...prev, calculatorSession: null }
        : prev)
    } catch (err) {
      console.error('delete estimate error:', err)
    } finally {
      setDeletingId(null)
    }
  }

  // Delegates to the shared resumeCalculatorDraft helper (src/lib) —
  // extracted this session so StartPage.js's own "Resume" action uses the
  // identical fetch-then-navigate logic instead of a second copy. Behavior
  // unchanged: still fetches fresh at click time, still keys off
  // owner_user_id/published_at/deleted_at, still passes state only (never
  // the draft's internal id in a URL — draft_publish_framework.md
  // invariant 5).
  async function handleResumeDraft(sessionId) {
    await resumeCalculatorDraft(navigate, user.id, sessionId)
  }

  if (loading) {
    return (
      <div className="asmt-state">
        <div className="app-spinner" />
      </div>
    )
  }

  const snap          = hubData?.snapshot        || {}
  const isUsPerson    = hubData?.isUsPerson      ?? false
  const rationale     = hubData?.rationale       || []
  const triageUrl     = hubData?.triageToken ? `/start/results/${hubData.triageToken}` : '/start'
  const hasTriage     = !!hubData?.triageToken
  const calc          = hubData?.calculatorSession
  // Shown whenever the user has at least one session to manage — including
  // a lone one, so deleting down from two to one doesn't make the whole
  // list vanish. Drafts are included too (see the query comment) since
  // they never appear in the main card above.
  const showPastEstimates = pastEstimates.length > 0

  const complexityLabel = { simple: 'Straightforward', moderate: 'Moderate', complex: 'Complex' }[snap.complexity] || null
  const complexityClass = { simple: 'hub-complexity-simple', moderate: 'hub-complexity-moderate', complex: 'hub-complexity-complex' }[snap.complexity] || ''

  const formatDate = (iso) => iso
    ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : ''

  const fmtEur = (n) => n != null ? `€${Math.round(n).toLocaleString('en')}` : null

  return (
    <div className="hub-page">

      {/* ── Header ── */}
      <div className="hub-header">
        <div>
          <div className="hub-eyebrow">Tax Intelligence Center</div>
          <h1 className="hub-title">Your tax intelligence, all in one place</h1>
          <p className="hub-subtitle">
            Start with your Spain-focused snapshot, then unlock your full US × Spain cross-border assessment.
          </p>
        </div>
        {activeTaxYear && (
          <div className="hub-year-badge">
            <div className="hub-year-label">Tax year under review</div>
            <div className="hub-year-value">{activeTaxYear}</div>
          </div>
        )}
      </div>

      {/* ── Spain Overview card ── */}
      <div className="hub-card hub-card-spain">
        <div className="hub-card-left">
          <div className="hub-card-title-row">
            <span>🇪🇸</span>
            <h2 className="hub-card-title">Spain Tax Snapshot</h2>
            {/* <span className="hub-card-badge hub-card-badge-included">Included</span> */}
            {hasTriage && <span className="hub-card-badge hub-card-badge-complete">✓ Complete</span>}
          </div>
          <p className="hub-card-desc">Your Spain-focused residency, filing, and reporting snapshot.</p>
          <ul className="hub-card-features">
            {['Spain tax residency analysis','Spain filing obligations & deadlines','Key reporting requirements (Modelos)','Personalised next steps for Spain'].map(f => (
              <li key={f} className="hub-card-feature">
                <span className="hub-card-feature-check">✓</span>{f}
              </li>
            ))}
          </ul>
          <Link to={triageUrl} className="btn-primary hub-card-btn">
            {hasTriage ? 'View Spain Snapshot →' : 'Build Spain Snapshot →'}
          </Link>
        </div>

        <div className="hub-glance">
          <div className="hub-glance-label">At a glance</div>
          {snap.residencyLabel && (
            <div className="hub-glance-row">
              <span className="hub-glance-icon">👤</span>
              <div>
                <div className="hub-glance-key">Residency status</div>
                <div className="u-label-sm">{snap.residencyLabel}</div>
              </div>
            </div>
          )}
          {snap.primaryReturn && (
            <div className="hub-glance-row">
              <span className="hub-glance-icon">📄</span>
              <div>
                <div className="hub-glance-key">Primary return</div>
                <div className="u-label-sm">{snap.primaryReturn}</div>
                <div className="hub-glance-sub">For {activeTaxYear}</div>
              </div>
            </div>
          )}
          {snap.keyDeadline && (
            <div className="hub-glance-row">
              <span className="hub-glance-icon">🏛</span>
              <div>
                <div className="hub-glance-key">Key deadline</div>
                <div className="u-label-sm">{snap.keyDeadline} {activeTaxYear ? activeTaxYear + 1 : ''}</div>
                {snap.primaryReturn && <div className="hub-glance-sub">{snap.primaryReturn} filing</div>}
              </div>
            </div>
          )}
          {complexityLabel && (
            <div className="hub-glance-row">
              <span className="hub-glance-icon">📊</span>
              <div>
                <div className="hub-glance-key">Complexity</div>
                <div className={`u-label-sm hub-glance-complexity ${complexityClass}`}>{complexityLabel}</div>
              </div>
            </div>
          )}
          {!snap.residencyLabel && !snap.primaryReturn && (
            <p className="hub-glance-empty">Complete your Spain snapshot to see your snapshot here.</p>
          )}
        </div>
      </div>

      {/* ── IRPF Calculator card ── */}
      <div className={`hub-card hub-card-calc${showPastEstimates ? ' hub-card-has-past' : ''}`}>
        <div className="hub-card-left">
          <div className="hub-card-title-row">
            {/* <span>🧮</span> */}
            <h2 className="hub-card-title">IRPF Tax Calculator</h2>
            {calc && <span className="hub-card-badge hub-card-badge-complete">✓ Complete</span>}
          </div>
          <p className="hub-card-desc">
            Get a step-by-step estimate of your Spanish income tax liability based on your specific income types and situation.
          </p>
          <ul className="hub-card-features">
            {[
              ['Income classification',  'Employment, rental, investments, pensions and more'],
              ['Scenario modelling',     'See how income classification choices affect your bill'],
              ['US-specific income',     '401k, IRA, K-1, S-corp treated separately'],
              ['Bases breakdown',        'General base vs savings base, applied at correct rates'],
            ].map(([title, sub]) => (
              <li key={title} className="hub-card-feature hub-card-feature-two-line">
                <span className="hub-card-feature-icon">📋</span>
                <div>
                  <div className="u-label-sm">{title}</div>
                  <div className="hub-card-feature-sub">{sub}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="hub-assess-panel">
          {calc ? (
            <>
              <div className="hub-calc-result">
                {calc.estimate_low > 0 && calc.estimate_high > 0 ? (
                  <>
                    <div className="hub-calc-result-label">Estimated IRPF range</div>
                    <div className="hub-calc-result-value">{fmtEur(calc.estimate_low)} – {fmtEur(calc.estimate_high)}</div>
                  </>
                ) : fmtEur(calc.irpf_gross) ? (
                  <>
                    <div className="hub-calc-result-label">Estimated IRPF</div>
                    <div className="hub-calc-result-value">{fmtEur(calc.irpf_gross)}</div>
                  </>
                ) : null}
                {calc.estimate_result?.effective_rate_on_total_income != null && (
                  <div className="hub-calc-result-rate">
                    Effective rate: {(calc.estimate_result.effective_rate_on_total_income).toFixed(1)}%
                  </div>
                )}
              </div>
              <Link to={`/calculator/result/${calc.public_token}`} className="btn-primary hub-assess-btn">
                View your {calc.tax_year} estimate →
              </Link>
              <Link to="/calculator" className="hub-calc-redo">Run a new estimate →</Link>
            </>
          ) : (
            <>
              <ul className="hub-assess-features">
                {['Detailed income classification','Scenario comparison','Bases and rate breakdown','Save and return anytime'].map(f => (
                  <li key={f} className="hub-assess-feature">
                    <span className="hub-assess-check">✓</span>{f}
                  </li>
                ))}
              </ul>
              <Link to="/calculator" className="btn-primary hub-assess-btn">
                Open IRPF Calculator →
              </Link>
            </>
          )}
        </div>
      </div>

      {showPastEstimates && (
        <div className="hub-past-estimates">
          <div className="hub-past-estimates-title">Your calculator scenarios</div>
          {pastEstimates.map(est => (
            <div key={est.id} className="hub-past-estimate-row">
              {est.published_at ? (
                <Link to={`/calculator/result/${est.public_token}`} className="hub-past-estimate-link">
                  {est.is_autonomo ? 'Autónomo' : 'IRPF'} {est.tax_year || ''} · {fmtEur(est.irpf_gross) || '—'} · {formatDate(est.published_at)}
                </Link>
              ) : (
                // Drafts never have a usable public_token (see
                // draft_publish_framework.md's invariant 4), so this is a
                // button that fetches-and-navigates, not a real Link — the
                // draft's internal id stays out of the URL (invariant 5),
                // passed only via router state in handleResumeDraft.
                // Drafts never have a usable public_token (see
                // draft_publish_framework.md's invariant 4), so this can't
                // be a real react-router Link with an href — it's a same-
                // page action (fetch, then navigate with state), styled as
                // a link via href="#" + preventDefault, same pattern
                // HomeLink.js already established for exactly this reason
                // (a <button> here picks up default chrome that
                // border:none/background:none doesn't fully clear in every
                // browser). The draft's internal id stays out of the URL
                // either way (invariant 5) — passed only via router state.
                <a
                  href="#"
                  className="hub-past-estimate-link"
                  onClick={e => { e.preventDefault(); handleResumeDraft(est.id) }}
                >
                  {est.is_autonomo ? 'Autónomo' : 'IRPF'} {est.tax_year || ''} · Draft · Created {formatDate(est.created_at)} · Resume →
                </a>
              )}
              <button
                className="hub-past-estimate-delete"
                onClick={() => handleDeleteEstimate(est.id)}
                disabled={deletingId === est.id}
                aria-label="Delete this estimate"
              >
                {deletingId === est.id ? '…' : '× Delete'}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── US × Spain Assessment card ── */}
      <div className={`hub-card hub-card-us ${!isUsPerson ? 'hub-card-muted' : ''}`}>
        <div className="hub-card-left">
          <div className="hub-card-title-row">
            <span>🇺🇸</span><span>🇪🇸</span>
            <h2 className="hub-card-title">US × Spain Assessment</h2>
          </div>
          <p className="hub-card-desc">
            A comprehensive dual-jurisdiction intelligence brief covering both your US and Spanish tax obligations.
          </p>
          <ul className="hub-card-features">
            {[
              ['US filing analysis',           'Form 1040, FBAR, FATCA, and more'],
              ['Spain filing analysis',         'IRPF, Patrimonio, Modelos, and reporting'],
              ['Cross-border coordination',     'FTC vs FEIE, social security, and interactions'],
              ['Timeline & residency analysis', 'Key dates, split-year rules, and transitions'],
              ['Planning insights',             'Opportunities and areas to review'],
            ].map(([title, sub]) => (
              <li key={title} className="hub-card-feature hub-card-feature-two-line">
                <span className="hub-card-feature-icon">📋</span>
                <div>
                  <div className="u-label-sm">{title}</div>
                  <div className="hub-card-feature-sub">{sub}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="hub-assess-panel">
          {/* {isUsPerson ? ( */}
          {1==0 ? (
            hasAssessment ? (
              <>
                <div className="hub-assess-exists">
                  <span className="hub-assess-exists-icon">✓</span>
                  <div className="hub-assess-exists-title">Assessment ready</div>
                  <div className="hub-assess-exists-date">Generated {formatDate(assessmentDate)}</div>
                </div>
                <button className="btn-primary hub-assess-btn" onClick={() => navigate('/assessment')}>
                  View your {activeTaxYear} assessment →
                </button>
              </>
            ) : (
              <>
                <ul className="hub-assess-features">
                  {['Deep dual-jurisdiction analysis','Personalised to your situation','Advisor-ready intelligence brief','Export and share with your tax advisor'].map(f => (
                    <li key={f} className="hub-assess-feature">
                      <span className="hub-assess-check">✓</span>{f}
                    </li>
                  ))}
                </ul>
                <button className="btn-primary hub-assess-btn" onClick={() => navigate('/assessment?generate=1')}>
                  Generate Full Assessment →
                </button>
                <p className="hub-assess-note">Takes ~60–90 seconds</p>
              </>
            )
          ) : (
            <div className="hub-assess-locked">
              <span className="hub-assess-lock">🔒</span>
              Under Construction
              <div className="u-label-strong">Available for US persons only</div>
              <p className="u-text-muted-xs">
                This assessment is designed for US citizens and green card holders living in Spain.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Cross-border rationale ── */}
      {isUsPerson && rationale.length > 0 && (
        <div className="hub-rationale">
          <div className="hub-rationale-title">Why your profile may go beyond a Spain-only snapshot</div>
          {rationale.map((s, i) => (
            <p key={i} className="hub-rationale-sentence">{s}</p>
          ))}
        </div>
      )}

      {/* ── Not a US person strip ── */}
      {!isUsPerson && (
        <div className="hub-not-us">
          <span className="hub-not-us-icon">💬</span>
          <div>
            <div className="u-card-title-sm">Not a US person?</div>
            <p className="u-text-muted-xs">You can still use your Spain snapshot to stay informed about your Spanish tax situation.</p>
          </div>
          <Link to={triageUrl} className="btn-primary hub-not-us-btn">
            {hasTriage ? 'View Spain Snapshot →' : 'Build Spain Snapshot →'}
          </Link>
        </div>
      )}

    </div>
  )
}